<template>
    <div class="base-input" :class="frameClass()" :style="frameStyle()">
        <!-- The frame draws the border and focus ring so prefix and suffix content share the
             field's box; the input inside is borderless. A caller's class and style dress the frame,
             and every other attr (placeholder, aria-label, name, disabled, listeners) reaches the input. -->
        <span v-if="$slots.prefix" class="affix">
            <slot name="prefix"></slot>
        </span>
        <!-- Labelled by a wrapping FormField via for/id, or by an aria-label callers pass
             bare via fallthrough attrs, which the a11y rule can't statically see. -->
        <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -->
        <input ref="control" v-bind="inputAttrs()" v-model="model" :type="type" :data-empty="model === '' || undefined" class="control fs-mask" />
        <span v-if="$slots.suffix" class="affix">
            <slot name="suffix"></slot>
        </span>
    </div>
</template>

<script lang="ts">
interface BaseInputProps extends /* @vue-ignore */ InputHTMLAttributes {
    /**
     * Single-line input types only; block-level types get their own component.
     * A search field is `type="search"` with the search glyph in the prefix slot.
     * A date field is `type="date"`: the native picker stays, the frame and FormField
     * wiring are shared, and `min`/`max` pass through to the input as `yyyy-MM-dd`.
     * A time field is `type="time"` the same way, with the model as 24-hour `HH:mm`.
     */
    type?: "text" | "email" | "tel" | "url" | "password" | "search" | "date" | "time";
    /**
     * `default` fills its container with small body type. `compact` is the narrow header and toolbar field.
     * `large` is the roomy single-question field (onboarding steps) with full body type and more padding.
     */
    size?: "default" | "compact" | "large";
    /** Weight of the typed text. `bold` is for a field that is the whole question on screen, such as onboarding's name step. */
    weight?: "regular" | "bold";
}
</script>

<script setup lang="ts">
import type { HTMLAttributes, InputHTMLAttributes, StyleValue } from "vue";
import { useAttrs, useTemplateRef } from "vue";

import { useFormField } from "./formFieldContext";

defineOptions({ inheritAttrs: false });

defineSlots<{
    /** Leading content inside the field, such as the search glyph. Mark icons aria-hidden. */
    prefix?(): unknown;
    /** Trailing content inside the field, such as an action button. Give any button an aria-label. */
    suffix?(): unknown;
}>();

const { type = "text", size = "default", weight = "regular" } = defineProps<BaseInputProps>();

const model = defineModel<string>({ default: "" });

const control = useTemplateRef<HTMLInputElement>("control");

defineExpose({
    /** Moves focus to the inner input, since a ref on BaseInput points at the frame. */
    focus: (options?: FocusOptions) => control.value?.focus(options),
});

// Wraps itself into a surrounding FormField when present; used bare it stays a plain
// styled input (placeholder, name, autocomplete, disabled, ... arrive via fallthrough).
const field = useFormField();

// These helpers run in the template, so every render reads the current attrs directly and a
// caller's :disabled or FormField's error reaches the input and frame without a cached copy.
const attrs = useAttrs();

// Caller attrs win over the FormField wiring, so an explicit id or aria-describedby still applies.
function inputAttrs(): Record<string, unknown> {
    const { class: _class, style: _style, ...rest } = attrs;
    return { ...field?.controlAttrs.value, ...rest };
}

// The frame shows the input's disabled and error states, since it is the element that draws them.
function frameClass(): HTMLAttributes["class"] {
    const input = inputAttrs();
    const isDisabled = input.disabled !== undefined && input.disabled !== false;
    const isInvalid = String(input["aria-invalid"]) === "true";
    return [sizeClasses[size], { "is-bold": weight === "bold", "is-disabled": isDisabled, "is-invalid": isInvalid }, attrs.class as HTMLAttributes["class"]];
}

function frameStyle(): StyleValue {
    return attrs.style as StyleValue;
}

const sizeClasses: Record<NonNullable<BaseInputProps["size"]>, string> = {
    default: "",
    compact: "is-compact",
    large: "is-large",
};
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    .base-input {
        @apply flex w-full items-center gap-2.5 rounded-lg border border-control-border bg-control-bg p-2.5 transition-colors focus-field-within;
    }

    .base-input.is-compact {
        @apply w-64 py-2;
    }

    .base-input.is-large {
        @apply p-4;
    }

    .base-input.is-disabled {
        @apply bg-control-disabled-bg;
    }

    .base-input.is-invalid {
        @apply border-status-error;
    }

    .base-input .control {
        @apply min-w-0 flex-1 border-none bg-transparent p-0 outline-none body-small text-control-text-strong placeholder:text-control-placeholder;
    }

    .base-input.is-large .control {
        @apply body;
    }

    .base-input.is-bold .control {
        @apply font-semibold;
    }

    /* The field draws its own affordances; drop the browser's clear and decoration for type="search". */
    .base-input .control::-webkit-search-cancel-button,
    .base-input .control::-webkit-search-decoration {
        @apply appearance-none;
    }

    /* Date and time inputs keep the native picker, but Chrome draws its indicator as a fixed dark
       glyph. Masking it with the matching icon lets it take the affix icon color token in both themes.
       Firefox and Safari expose no indicator pseudo-element and keep their own picker control. */
    .base-input .control {
        --picker-icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath d='M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5M1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4z'/%3E%3C/svg%3E");
    }

    .base-input .control[type="time"] {
        --picker-icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath d='M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71z'/%3E%3Cpath d='M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16m7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0'/%3E%3C/svg%3E");
    }

    .base-input .control::-webkit-calendar-picker-indicator {
        @apply m-0 size-4 shrink-0 cursor-pointer bg-control-text bg-none p-0 opacity-100;
        -webkit-mask: var(--picker-icon) center / contain no-repeat;
        mask: var(--picker-icon) center / contain no-repeat;
    }

    /* A disabled date or time keeps its glyph but drops the pointer and takes the disabled text color. */
    .base-input.is-disabled .control::-webkit-calendar-picker-indicator {
        @apply cursor-default bg-control-disabled-text;
    }

    /* Chrome pads the date and time segments on its own; the frame already provides the field's padding. */
    .base-input .control::-webkit-datetime-edit {
        @apply p-0;
    }

    /* An empty date or time shows Chrome's mm/dd/yyyy or --:-- -- mask; placeholder color keeps it from reading as answered. */
    .base-input .control[data-empty]::-webkit-datetime-edit {
        @apply text-control-placeholder;
    }

    .base-input .affix {
        @apply flex shrink-0 items-center text-base/none text-control-text;
    }
}
</style>
